import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Wand2, X } from 'lucide-react';
import { buildAppDateTimeIso, createClientRequestId, toAppDateInputValue, toAppTimeInputValue } from '../lib/appDateTime';
import { DEFAULT_EXAM_TYPES } from '../lib/examTypes';

const API = process.env.REACT_APP_BACKEND_URL || '';
const CREATE_NEW_VALUE = '__create_new__';
const CUSTOM_TEMPLATES_STORAGE_KEY = 'cras_custom_exam_templates';
const QUALITATIVE_RESULT_OPTIONS = ['Positivo', 'Negativo', 'Inconclusivo', 'Não se aplica'];
const INTENSITY_OPTIONS = ['Ausente', 'Leve', 'Moderada', 'Intensa', 'Rara', '+', '++', '+++'];
const TYPE_SPECIFIC_TEMPLATES = {
  Parasitologia: [
    { nome_parametro: 'Resultado parasitológico', valor: '', unidade: '', referencia: 'Negativo', resultado: '', intensidade: '', agente_identificado: '', observacoes: '' },
    { nome_parametro: 'Identificação do agente', valor: '', unidade: '', referencia: '', resultado: '', intensidade: '', agente_identificado: '', observacoes: '' }
  ],
  'Parasitologia de swab de traqueia': [
    { nome_parametro: 'Resultado do swab de traqueia', valor: '', unidade: '', referencia: 'Negativo', resultado: '', intensidade: '', agente_identificado: '', observacoes: '' },
    { nome_parametro: 'Agente identificado', valor: '', unidade: '', referencia: '', resultado: '', intensidade: '', agente_identificado: '', observacoes: '' }
  ],
  Hemoparasitas: [
    { nome_parametro: 'Pesquisa de hemoparasitas', valor: '', unidade: '', referencia: 'Negativo', resultado: '', intensidade: '', agente_identificado: '', observacoes: '' },
    { nome_parametro: 'Agente identificado', valor: '', unidade: '', referencia: '', resultado: '', intensidade: '', agente_identificado: '', observacoes: '' }
  ],
  Microbiologia: [
    { nome_parametro: 'Crescimento microbiológico', valor: '', unidade: '', referencia: 'Negativo', resultado: '', intensidade: '', agente_identificado: '', observacoes: '' },
    { nome_parametro: 'Agente identificado', valor: '', unidade: '', referencia: '', resultado: '', intensidade: '', agente_identificado: '', observacoes: '' }
  ]
};


const toDateTimeLocal = (value) => {
  if (!value) return '';
  const dateValue = toAppDateInputValue(value);
  const timeValue = toAppTimeInputValue(value);
  return dateValue && timeValue ? `${dateValue}T${timeValue}` : '';
};

const mapExamParams = (params = []) => params.map((param) => ({
  nome_parametro: param.nome_parametro || '',
  valor: param.valor || '',
  unidade: param.unidade || '',
  referencia: param.referencia || '',
  resultado: param.resultado || '',
  intensidade: param.intensidade || '',
  agente_identificado: param.agente_identificado || '',
  observacoes: param.observacoes || ''
}));

export default function ExamForm({
  animalId,
  user,
  editingExam = null,
  initialValues = {},
  existingExams = [],
  contextHeader = null,
  submitLabel,
  onSaved,
  onCancel
}) {
  const [loading, setLoading] = useState(false);
  const [tipoExame, setTipoExame] = useState(initialValues.tipo_exame || '');
  const [categoria, setCategoria] = useState(initialValues.categoria || 'Interno');
  const [laboratorioOrigem, setLaboratorioOrigem] = useState(initialValues.laboratorio_origem || '');
  const [realizadoEm, setRealizadoEm] = useState(initialValues.realizado_em || 'Animal vivo');
  const [dataColeta, setDataColeta] = useState(initialValues.data_coleta || '');
  const [dataResultado, setDataResultado] = useState(initialValues.data_resultado || '');
  const [responsavel, setResponsavel] = useState(initialValues.responsavel || '');
  const [observacoes, setObservacoes] = useState(initialValues.observacoes || '');
  const [parametros, setParametros] = useState(mapExamParams(initialValues.parametros || []));
  const [nomeParametro, setNomeParametro] = useState('');
  const [valorParametro, setValorParametro] = useState('');
  const [unidadeParametro, setUnidadeParametro] = useState('');
  const [referenciaParametro, setReferenciaParametro] = useState('');
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [creatingExamType, setCreatingExamType] = useState(false);
  const [examClientRequestId, setExamClientRequestId] = useState(() => createClientRequestId('exam'));

  useEffect(() => {
    if (editingExam) {
      setTipoExame(editingExam.tipo_exame || '');
      setCategoria(editingExam.categoria || 'Interno');
      setLaboratorioOrigem(editingExam.laboratorio_origem || '');
      setRealizadoEm(editingExam.realizado_em || 'Animal vivo');
      setDataColeta(toDateTimeLocal(editingExam.data_coleta));
      setDataResultado(toDateTimeLocal(editingExam.data_resultado));
      setResponsavel(editingExam.responsavel || '');
      setObservacoes(editingExam.observacoes || '');
      setParametros(mapExamParams(editingExam.parametros || []));
      setCreatingExamType(false);
    }
  }, [editingExam]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(CUSTOM_TEMPLATES_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setCustomTemplates(parsed);
      }
    } catch (error) {
      console.error('Error loading custom templates:', error);
    }
  }, []);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await axios.get(`${API}/api/exam-templates`);
        setAvailableTemplates(response.data.templates || []);
      } catch (error) {
        console.error('Error fetching templates:', error);
      }
    };
    fetchTemplates();
  }, []);

  const examTypeOptions = [...new Set([
    ...DEFAULT_EXAM_TYPES,
    ...availableTemplates.map((template) => template.exam_type || template.name),
    ...customTemplates.map((template) => template.exam_type),
    ...existingExams.map((exam) => exam.tipo_exame),
    tipoExame
  ].filter(Boolean))];

  const handleExamTypeChange = (value) => {
    if (value === CREATE_NEW_VALUE) {
      setCreatingExamType(true);
      setTipoExame('');
      return;
    }
    setCreatingExamType(false);
    setTipoExame(value);
  };

  const saveCustomTemplates = (templates) => {
    setCustomTemplates(templates);
    localStorage.setItem(CUSTOM_TEMPLATES_STORAGE_KEY, JSON.stringify(templates));
  };

  const handleLoadTemplate = async () => {
    if (!tipoExame) {
      toast.error('Selecione um tipo de exame primeiro');
      return;
    }
    setLoadingTemplate(true);
    try {
      const customTemplate = customTemplates.find((template) => template.exam_type === tipoExame);
      if (customTemplate) {
        const templateParams = (customTemplate.parameters || []).map((param) => ({
          nome_parametro: param.nome_parametro || param.nome || '',
          valor: '',
          unidade: param.unidade || '',
          referencia: param.referencia || '',
          resultado: param.resultado || '',
          intensidade: param.intensidade || '',
          agente_identificado: param.agente_identificado || '',
          observacoes: param.observacoes || ''
        }));
        setParametros(templateParams);
        toast.success(`Template personalizado carregado com ${templateParams.length} parâmetros!`);
        return;
      }
      const response = await axios.get(`${API}/api/exam-templates/${tipoExame}/parameters`);
      if (response.data.parameters && response.data.parameters.length > 0) {
        setParametros(response.data.parameters.map((param) => ({
          nome_parametro: param.nome,
          valor: '',
          unidade: param.unidade,
          referencia: param.referencia,
          resultado: '',
          intensidade: '',
          agente_identificado: '',
          observacoes: ''
        })));
        toast.success(`Template carregado com ${response.data.parameters.length} parâmetros!`);
      } else if (TYPE_SPECIFIC_TEMPLATES[tipoExame]) {
        setParametros(TYPE_SPECIFIC_TEMPLATES[tipoExame].map((param) => ({ ...param })));
        toast.success(`Template carregado com ${TYPE_SPECIFIC_TEMPLATES[tipoExame].length} parâmetros!`);
      } else {
        toast.info('Nenhum template disponível para este tipo de exame');
      }
    } catch (error) {
      console.error('Error loading template:', error);
      toast.error('Erro ao carregar template');
    } finally {
      setLoadingTemplate(false);
    }
  };

  const handleSaveCurrentAsTemplate = () => {
    if (!tipoExame) {
      toast.error('Selecione ou cadastre um tipo de exame primeiro');
      return;
    }
    const sourceParams = parametros.length > 0
      ? parametros
      : (nomeParametro ? [{ nome_parametro: nomeParametro, unidade: unidadeParametro, referencia: referenciaParametro }] : []);
    const sanitizedParams = sourceParams
      .map((param) => ({
        nome_parametro: (param.nome_parametro || '').trim(),
        unidade: param.unidade || '',
        referencia: param.referencia || ''
      }))
      .filter((param) => param.nome_parametro);
    if (sanitizedParams.length === 0) {
      toast.error('Adicione ao menos um parâmetro para salvar o template');
      return;
    }
    const nextTemplates = customTemplates.filter((template) => template.exam_type !== tipoExame);
    nextTemplates.push({
      exam_type: tipoExame,
      name: `Template ${tipoExame}`,
      category: categoria,
      parameters: sanitizedParams
    });
    saveCustomTemplates(nextTemplates);
    toast.success('Template personalizado salvo com sucesso');
  };

  const handleAddParameter = () => {
    if (!nomeParametro || !valorParametro) {
      toast.error('Parâmetro e resultado são obrigatórios');
      return;
    }
    setParametros([...parametros, {
      nome_parametro: nomeParametro,
      valor: valorParametro,
      unidade: unidadeParametro,
      referencia: referenciaParametro,
      resultado: '',
      intensidade: '',
      agente_identificado: '',
      observacoes: ''
    }]);
    setNomeParametro('');
    setValorParametro('');
    setUnidadeParametro('');
    setReferenciaParametro('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;
    if (!tipoExame || !dataColeta) {
      toast.error('Tipo de exame e data de coleta são obrigatórios');
      return;
    }
    if (categoria === 'Externo' && !laboratorioOrigem) {
      toast.error('Laboratório de origem é obrigatório para exames externos');
      return;
    }
    const payload = {
      tipo_exame: tipoExame,
      categoria,
      laboratorio_origem: laboratorioOrigem || null,
      realizado_em: realizadoEm,
      data_coleta: buildAppDateTimeIso(dataColeta.slice(0, 10), dataColeta.slice(11, 16)),
      data_resultado: dataResultado ? buildAppDateTimeIso(dataResultado.slice(0, 10), dataResultado.slice(11, 16)) : null,
      responsavel: responsavel || user?.name || 'Sistema',
      observacoes: observacoes || null,
      parametros
    };
    setLoading(true);
    try {
      let response;
      if (editingExam) {
        response = await axios.put(`${API}/api/exams/${editingExam.id}`, payload);
      } else {
        response = await axios.post(`${API}/api/exams`, {
          animal_id: animalId,
          client_request_id: examClientRequestId,
          lab_request_id: initialValues.lab_request_id || null,
          requested_exam_type: initialValues.requested_exam_type || tipoExame,
          ...payload
        });
        setExamClientRequestId(createClientRequestId('exam'));
      }
      toast.success(editingExam ? 'Exame atualizado com sucesso!' : 'Exame registrado com sucesso.');
      onSaved?.(response.data);
    } catch (error) {
      console.error('Error saving exam:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar exame');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="shared-exam-form">
      {contextHeader}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Tipo de Exame *</Label>
          <Select value={creatingExamType ? CREATE_NEW_VALUE : tipoExame} onValueChange={handleExamTypeChange}>
            <SelectTrigger data-testid="exam-type-select"><SelectValue placeholder="Selecione o tipo" /></SelectTrigger>
            <SelectContent>
              {examTypeOptions.map((type) => <SelectItem key={type} value={type}>{type}</SelectItem>)}
              <SelectItem value={CREATE_NEW_VALUE}>+ Cadastrar novo tipo</SelectItem>
            </SelectContent>
          </Select>
          {creatingExamType && (
            <Input className="mt-2" placeholder="Digite o novo tipo de exame" value={tipoExame} onChange={(event) => setTipoExame(event.target.value)} data-testid="new-exam-type-input" />
          )}
        </div>
        <div>
          <Label>Categoria *</Label>
          <Select value={categoria} onValueChange={setCategoria}>
            <SelectTrigger data-testid="exam-category-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Interno">Interno</SelectItem>
              <SelectItem value="Externo">Externo</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {categoria === 'Externo' && (
          <div>
            <Label>Laboratório de Origem *</Label>
            <Input value={laboratorioOrigem} onChange={(event) => setLaboratorioOrigem(event.target.value)} placeholder="Nome do laboratório" data-testid="lab-origin-input" />
          </div>
        )}
        <div>
          <Label>Realizado em *</Label>
          <Select value={realizadoEm} onValueChange={setRealizadoEm}>
            <SelectTrigger data-testid="exam-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Animal vivo">Animal vivo</SelectItem>
              <SelectItem value="Animal morto">Animal morto</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Data de Coleta *</Label>
          <Input type="datetime-local" value={dataColeta} onChange={(event) => setDataColeta(event.target.value)} data-testid="collection-date-input" />
        </div>
        <div>
          <Label>Data do Resultado</Label>
          <Input type="datetime-local" value={dataResultado} onChange={(event) => setDataResultado(event.target.value)} data-testid="result-date-input" />
        </div>
        <div>
          <Label>Responsável</Label>
          <Input value={responsavel} onChange={(event) => setResponsavel(event.target.value)} placeholder={user?.name} data-testid="responsible-input" />
        </div>
      </div>
      <div>
        <Label>Resultado / Observações do Laudo</Label>
        <Textarea
          className="min-h-[120px] border-emerald-300 bg-emerald-50/40 text-base"
          value={observacoes}
          onChange={(event) => setObservacoes(event.target.value)}
          placeholder="Registre o resultado principal, interpretação, conclusão ou observações adicionais do exame"
          data-testid="observations-textarea"
        />
      </div>
      {tipoExame && (
        <div className="border-t pt-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 gap-2">
            <h3 className="font-semibold text-emerald-800">Resultados Estruturados</h3>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" onClick={handleLoadTemplate} disabled={loadingTemplate || !tipoExame} className="border-emerald-600 text-emerald-600 hover:bg-emerald-50" data-testid="load-template-btn">
                {loadingTemplate ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />}
                Carregar Template
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={handleSaveCurrentAsTemplate} className="border-blue-600 text-blue-600 hover:bg-blue-50" data-testid="save-template-btn">
                + Cadastrar Template
              </Button>
            </div>
          </div>
          {parametros.length > 0 && (
            <div className="mb-4 space-y-3 max-h-80 overflow-y-auto pr-2">
              {parametros.map((param, index) => (
                <div key={index} className="grid grid-cols-1 gap-2 bg-gray-50 p-3 rounded-lg sm:grid-cols-12">
                  <div className="sm:col-span-4">
                    <Label className="text-xs text-gray-500">Parâmetro</Label>
                    <Input value={param.nome_parametro} onChange={(event) => {
                      const next = [...parametros];
                      next[index].nome_parametro = event.target.value;
                      setParametros(next);
                    }} className="h-9 font-medium" data-testid={`param-name-${index}`} />
                    <p className="text-xs text-emerald-600">Ref: {param.referencia || '-'}</p>
                  </div>
                  <div className="sm:col-span-4">
                    <Input type="text" placeholder="Resultado" aria-label={`Resultado de ${param.nome_parametro || 'parâmetro'}`} list="exam-result-value-options" value={param.valor} onChange={(event) => {
                      const next = [...parametros];
                      next[index].valor = event.target.value;
                      setParametros(next);
                    }} className="h-9" data-testid={`param-value-${index}`} />
                  </div>
                  <div className="sm:col-span-3">
                    <Input placeholder="Unidade" value={param.unidade} onChange={(event) => {
                      const next = [...parametros];
                      next[index].unidade = event.target.value;
                      setParametros(next);
                    }} className="h-9 text-sm" data-testid={`param-unit-${index}`} />
                  </div>
                  <div className="sm:col-span-1 flex justify-end sm:items-start">
                    <Button type="button" variant="ghost" size="sm" onClick={() => setParametros(parametros.filter((_, itemIndex) => itemIndex !== index))} className="text-red-600 h-8 w-8 p-0" title="Remover parâmetro">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="sm:col-span-3">
                    <Select value={param.resultado || ''} onValueChange={(value) => {
                      setParametros((previous) => previous.map((item, itemIndex) => (
                        itemIndex === index ? {
                          ...item,
                          resultado: value,
                          valor: !item.valor || QUALITATIVE_RESULT_OPTIONS.includes(item.valor) ? value : item.valor,
                        } : item
                      )));
                    }}>
                      <SelectTrigger className="h-9 text-sm" data-testid={`param-result-${index}`}><SelectValue placeholder="Resultado" /></SelectTrigger>
                      <SelectContent>{QUALITATIVE_RESULT_OPTIONS.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-3">
                    <Select value={param.intensidade || ''} onValueChange={(value) => {
                      const next = [...parametros];
                      next[index].intensidade = value;
                      setParametros(next);
                    }}>
                      <SelectTrigger className="h-9 text-sm" data-testid={`param-intensity-${index}`}><SelectValue placeholder="Intensidade" /></SelectTrigger>
                      <SelectContent>{INTENSITY_OPTIONS.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-3">
                    <Input placeholder="Agente identificado" value={param.agente_identificado || ''} onChange={(event) => {
                      const next = [...parametros];
                      next[index].agente_identificado = event.target.value;
                      setParametros(next);
                    }} className="h-9 text-sm" data-testid={`param-agent-${index}`} />
                  </div>
                  <div className="sm:col-span-3">
                    <Input placeholder="Referência" value={param.referencia || ''} onChange={(event) => {
                      const next = [...parametros];
                      next[index].referencia = event.target.value;
                      setParametros(next);
                    }} className="h-9 text-sm" data-testid={`param-ref-${index}`} />
                  </div>
                  <div className="sm:col-span-9">
                    <Input placeholder="Observação do parâmetro" value={param.observacoes || ''} onChange={(event) => {
                      const next = [...parametros];
                      next[index].observacoes = event.target.value;
                      setParametros(next);
                    }} className="h-9 text-sm" data-testid={`param-notes-${index}`} />
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="border-t pt-3 mt-3">
            <p className="text-sm text-gray-600 mb-2">Adicionar parâmetro manualmente:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 mb-3">
              <Input placeholder="Parâmetro *" value={nomeParametro} onChange={(event) => setNomeParametro(event.target.value)} data-testid="param-name-input" />
              <Input type="text" placeholder="Resultado *" aria-label="Resultado do novo parâmetro" list="exam-result-value-options" value={valorParametro} onChange={(event) => setValorParametro(event.target.value)} data-testid="param-value-input" />
              <Input placeholder="Unidade" value={unidadeParametro} onChange={(event) => setUnidadeParametro(event.target.value)} data-testid="param-unit-input" />
              <Input placeholder="Referência" value={referenciaParametro} onChange={(event) => setReferenciaParametro(event.target.value)} data-testid="param-ref-input" />
            </div>
            <datalist id="exam-result-value-options">
              {QUALITATIVE_RESULT_OPTIONS.map((option) => <option key={option} value={option} />)}
            </datalist>
            <p className="mb-3 text-xs text-gray-500">O resultado aceita Negativo, Positivo ou números, inclusive negativos (ex.: -2,5).</p>
            <Button type="button" onClick={handleAddParameter} variant="outline" size="sm" data-testid="add-param-btn">+ Adicionar Parâmetro</Button>
          </div>
        </div>
      )}
      <div className="flex flex-col sm:flex-row justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} className="min-h-[44px]">Cancelar</Button>
        <Button type="submit" disabled={loading} className="bg-emerald-600 min-h-[44px]" data-testid="submit-exam-btn">
          {loading ? 'Salvando...' : (submitLabel || (editingExam ? 'Salvar Alterações' : 'Criar Exame'))}
        </Button>
      </div>
    </form>
  );
}
